// Image loader utility for letter and number graphics
// This approach uses dynamic imports which work better with React's build system

// Cache for loaded images to avoid repeated imports
const imageCache = new Map();

// ASCII codes for A-Z are 65-90
const LETTER_ASCII_START = 65; // A
const LETTER_ASCII_END = 90;   // Z

// Danish special characters and their ASCII codes
const DANISH_LETTERS = {
  'Å': 197,  // A with ring above
  'Æ': 198,  // AE ligature
  'Ø': 216   // O with stroke
};

// Available number images 1-10
const NUMBER_MIN = 1;
const NUMBER_MAX = 10;

/**
 * Dynamically import a letter image
 * Supports A-Z (ASCII 65-90) and Danish special characters (Æ, Ø, Å)
 * @param {string} letter - The letter (A-Z, Æ, Ø, Å)
 * @returns {Promise<string>} - Promise that resolves to the image URL
 */
export const loadLetterImage = async (letter) => {
  const cacheKey = `letter_${letter}`;

  if (imageCache.has(cacheKey)) {
    return imageCache.get(cacheKey);
  }

  try {
    const asciiCode = letter.charCodeAt(0);

    // Check if it's a Danish special character
    const isDanishLetter = DANISH_LETTERS[letter] !== undefined;

    // Validate ASCII code is in valid range (A-Z or Danish special)
    const isValidEnglish = asciiCode >= LETTER_ASCII_START && asciiCode <= LETTER_ASCII_END;

    if (!isValidEnglish && !isDanishLetter) {
      throw new Error(`Invalid letter: ${letter} (ASCII ${asciiCode})`);
    }

    // Import the image using the ASCII code
    const imageModule = await import(`../assets/l_${asciiCode}.png`);
    const imageUrl = imageModule.default;

    imageCache.set(cacheKey, imageUrl);
    return imageUrl;
  } catch (error) {
    console.warn(`Failed to load letter image for ${letter} (ASCII ${letter.charCodeAt(0)}):`, error);
    imageCache.set(cacheKey, null);
    return null;
  }
};

/**
 * Dynamically import a number image
 * @param {number} number - The number (1-10)
 * @returns {Promise<string>} - Promise that resolves to the image URL
 */
export const loadNumberImage = async (number) => {
  const cacheKey = `number_${number}`;
  
  if (imageCache.has(cacheKey)) {
    return imageCache.get(cacheKey);
  }
  
  try {
    // Validate number is in valid range
    if (number < NUMBER_MIN || number > NUMBER_MAX) {
      throw new Error(`Invalid number: ${number}`);
    }
    
    const imageModule = await import(`../assets/n_${number}.png`);
    const imageUrl = imageModule.default;
    
    imageCache.set(cacheKey, imageUrl);
    return imageUrl;
  } catch (error) {
    console.warn(`Failed to load number image for ${number}:`, error);
    imageCache.set(cacheKey, null);
    return null;
  }
};

/**
 * Preload all letter and number images
 * This can be called on app startup to cache all images
 */
export const preloadAllImages = async () => {
  const promises = [];

  // Preload all letter images A-Z
  for (let ascii = LETTER_ASCII_START; ascii <= LETTER_ASCII_END; ascii++) {
    const letter = String.fromCharCode(ascii);
    promises.push(loadLetterImage(letter));
  }

  // Preload Danish special characters
  Object.keys(DANISH_LETTERS).forEach(letter => {
    promises.push(loadLetterImage(letter));
  });

  // Preload all number images 1-10
  for (let num = NUMBER_MIN; num <= NUMBER_MAX; num++) {
    promises.push(loadNumberImage(num));
  }

  try {
    await Promise.all(promises);
    console.log('All letter and number images preloaded successfully (including Danish characters)');
  } catch (error) {
    console.warn('Some images failed to preload:', error);
  }
};

/**
 * Get cached image URL synchronously (returns null if not cached)
 * @param {string} type - 'letter' or 'number'
 * @param {string|number} value - The letter or number
 * @returns {string|null} - The cached image URL or null
 */
export const getCachedImage = (type, value) => {
  const cacheKey = `${type}_${value}`;
  return imageCache.get(cacheKey) || null;
};

/**
 * Clear the image cache
 */
export const clearImageCache = () => {
  imageCache.clear();
};
